


























































import { Component, Vue, Prop } from 'vue-property-decorator';
import { getCompanies, getCompany } from '@/api/api';
import { companyTypes } from '@/data/companyTypes';
import get from 'lodash/get';
import moment from 'moment';
import * as XLSX from 'xlsx';
import MetaDatatable from '@/models/MetaDatatable';

@Component
export default class CompanyExportModal extends Vue {
    @Prop({ default: false }) value!: boolean;
    @Prop({ default: null }) meta!: MetaDatatable | null;

    filterOption: string = 'all';
    isGenerating: boolean = false;
    isDone: boolean = false;
    exportError: string = '';
    progress: number = 0;
    status: string = '';

    filterOptions = [
        {
            value: 'all',
            label: 'Toutes les entreprises',
            desc: '',
        },
        {
            value: 'nb_programs',
            label: 'Nb Total Progs > 0',
            desc: '— entreprises ayant au moins un programme',
        },
        {
            value: 'diffused',
            label: 'Nb Programmes Diffusés > 0',
            desc: '— entreprises avec des programmes diffusés',
        },
        {
            value: 'both',
            label: 'Les deux conditions',
            desc: '— Nb Total Progs > 0 ET Nb Programmes Diffusés > 0',
        },
    ];

    close() {
        if (this.isGenerating) return;
        this.$emit('input', false);
    }

    reset() {
        this.isGenerating = false;
        this.isDone = false;
        this.exportError = '';
        this.progress = 0;
        this.status = '';
    }

    async generate() {
        this.isGenerating = true;
        this.isDone = false;
        this.exportError = '';
        this.progress = 5;
        this.status = 'Récupération de la liste des entreprises...';

        try {
            const exportMeta: MetaDatatable = {
                total: 0,
                pages: 0,
                page: 0,
                perPage: 9999,
                search: this.meta ? this.meta.search : null,
                orderBy: this.meta ? this.meta.orderBy : null,
                orderWay: this.meta ? this.meta.orderWay : null,
                filters: this.meta ? this.meta.filters : null,
            };
            const { data: listResponse } = await getCompanies(exportMeta);

            let listCompanies: any[] = listResponse.data || [];

            if (this.filterOption === 'nb_programs') {
                listCompanies = listCompanies.filter((c: any) => c.nb_programs > 0);
            } else if (this.filterOption === 'diffused') {
                listCompanies = listCompanies.filter((c: any) => c.nb_total_programs_diffused > 0);
            } else if (this.filterOption === 'both') {
                listCompanies = listCompanies.filter(
                    (c: any) => c.nb_programs > 0 && c.nb_total_programs_diffused > 0
                );
            }

            this.progress = 20;
            this.status = 'Récupération des détails (0/' + listCompanies.length + ')...';

            // Fetch full detail for each company (Default group has all fields).
            // nb_programs is a VirtualProperty only set by the list call, so we merge it back.
            const BATCH = 10;
            const companies: any[] = [];
            let skipped = 0;
            for (let i = 0; i < listCompanies.length; i += BATCH) {
                const batch = listCompanies.slice(i, i + BATCH);
                const results = await Promise.all(
                    batch.map(function(c: any) {
                        return getCompany(c.id).then(function(r: any) {
                            r.data.nb_programs = c.nb_programs;
                            return { ok: true as true, value: r.data };
                        }).catch(function(err: any) {
                            console.warn('Company fetch failed, skipping id=' + c.id + ':', err);
                            return { ok: false as false, reason: err };
                        });
                    })
                );
                for (const result of results) {
                    if (result.ok) {
                        companies.push(result.value);
                    } else {
                        skipped++;
                    }
                }
                const fetched = Math.min(i + BATCH, listCompanies.length);
                this.progress = 20 + Math.round((fetched / listCompanies.length) * 50);
                this.status = 'Récupération des détails (' + fetched + '/' + listCompanies.length + ')' + (skipped > 0 ? ' — ' + skipped + ' ignorée(s)' : '') + '...';
            }

            this.progress = 72;
            this.status = 'Génération du fichier Excel (' + companies.length + ' entreprises)...';

            const rows = companies.map((c: any) => this.mapCompanyToRow(c));

            this.progress = 88;

            const ws = XLSX.utils.json_to_sheet(rows);

            if (rows.length > 0) {
                const keys = Object.keys(rows[0]);
                ws['!cols'] = keys.map(function(key) {
                    const wch = rows.reduce(function(max: number, r: any) {
                        const v = r[key];
                        return Math.max(max, String(v !== null && v !== undefined ? v : '').length + 2);
                    }, key.length + 2);
                    return { wch };
                });
            }

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Entreprises');

            this.progress = 96;
            this.status = 'Téléchargement en cours...';

            XLSX.writeFile(wb, 'entreprises_' + moment().format('YYYY-MM-DD') + '.xlsx');

            this.progress = 100;
            this.isDone = true;
            this.status = 'Export terminé — ' + rows.length + ' entreprise(s) exportée(s)' + (skipped > 0 ? ', ' + skipped + ' ignorée(s) (erreur)' : '') + '.';

            setTimeout(() => {
                this.$emit('input', false);
                this.reset();
            }, 4000);
        } catch (error) {
            console.error('Export Excel error:', error);
            this.exportError = "Erreur lors de l'export. Veuillez réessayer.";
            this.status = "Échec de l'export";
            this.progress = 0;
            this.isGenerating = false;
        }
    }

    private s(val: any): string {
        const str = val !== null && val !== undefined ? String(val) : '';
        return str.length > 32767 ? str.slice(0, 32767) : str;
    }

    private boolLabel(val: any): string {
        if (val === true || val === 'true') return 'Oui';
        if (val === false || val === 'false') return 'Non';
        return '';
    }

    private signatureLabel(val: string): string {
        if (val === 'pending') return 'En attente';
        if (val === 'ok') return 'OK';
        if (val === 'no') return 'Non';
        return val || '';
    }

    private fluxLabel(val: string): string {
        const map: any = {
            api: 'API',
            email: 'Email',
            web_interface: 'Interface web',
            sftp: 'Sftp',
        };
        return val && map[val] ? map[val] : this.s(val);
    }

    private mandatLabel(c: any): string {
        const num = get(c, 'mandat.num_mandat');
        if (typeof num === 'undefined') {
            if (!c.is_multi_mandat || c.is_multi_mandat === '') return 'NC';
            return 'MR';
        }
        return String(num);
    }

    private dateLabel(val: any): string {
        return val ? moment(val).format('L') : '';
    }

    private mapCompanyToRow(c: any): any {
        return {
            // === IDENTIFICATION ===
            'Nom': this.s(c.name),
            "Type d'entreprise": get(companyTypes, c.type, this.s(c.type)),
            'Forme juridique': this.s(c.legal_form),
            'SIRET': this.s(c.siret),
            'BIC': this.s(c.bic),
            'RCS': this.s(c.rcs),
            'Capital social': this.s(c.share_capital),
            'Note Promoteur': c.nb_of_stars ? c.nb_of_stars + ' étoiles' : 'Pas de note',
            'Commentaire Promoteur': this.s(c.promotor_note_comments),
            'Présentation': this.s(c.description),

            // === ADRESSE ===
            'Adresse': this.s(get(c, 'address.street')),
            'Adresse (ligne 2)': this.s(get(c, 'address.street2')),
            'Code postal': this.s(get(c, 'address.postal_code')),
            'Ville': this.s(get(c, 'address.city')),

            // === CONTACT ===
            'Email général': this.s(c.email),
            'Téléphone': this.s(c.phone),
            'Mobile': this.s(c.mobile),
            'Fax': this.s(c.fax),
            'Site partenaire': this.s(c.url),
            'Login site': this.s(c.login_site),

            // === DIRECTION COMMERCIALE ===
            'Directeur commercial': this.s(c.full_name_commercial_director),
            'Email directeur commercial': this.s(c.mail_commercial_director),
            'Tel mobile directeur commercial': this.s(c.phone_nb_commercial_director),

            // === RESPONSABLE VENTES ===
            'Responsable des ventes': this.s(c.commercial_manager_name),
            'Email responsable ventes': this.s(c.commercial_manager_email),
            'Tel fixe ventes': this.s(c.commercial_manager_phone),
            'Tel mobile ventes': this.s(c.commercial_manager_mobile),

            // === RESPONSABLE COMPTABILITÉ ===
            'Responsable comptabilité': this.s(c.account_manager_name),
            'Email comptabilité': this.s(c.account_manager_email),
            'Tel fixe compta': this.s(c.account_manager_phone),
            'Tel mobile compta': this.s(c.account_manager_mobile),

            // === BUSINESS ===
            'Business Developper': this.s(get(c, 'business_developper.firstname')),
            'Entreprise parente': this.s(get(c, 'holding.name', get(c, 'holding'))),

            // === PROGRAMMES ===
            'Nb Total Progs': c.nb_programs !== null && c.nb_programs !== undefined ? c.nb_programs : 0,
            'Nb Programmes Diffusés': c.nb_total_programs_diffused !== null && c.nb_total_programs_diffused !== undefined ? c.nb_total_programs_diffused : 0,
            'Nb prog à saisir': c.nb_programs_to_enter !== null && c.nb_programs_to_enter !== undefined ? c.nb_programs_to_enter : 0,
            'Dernière Sauv. Maj': this.dateLabel(c.contacted_at),

            // === MANDAT ===
            'Mandat prélèvement': this.mandatLabel(c),
            'N° Mandat': this.s(get(c, 'mandat.num_mandat')),
            'Échéance mandat': this.dateLabel(get(c, 'mandat.date_due')),

            // === API / FLUX ===
            'Espace partenaire': this.boolLabel(c.api_available),
            'Flux connecté': this.boolLabel(c.api_connected),
            'Méthode de flux': this.fluxLabel(c.flux_methode),

            // === COMMERCIAL ===
            'Honoraires HT': c.is_multi_fee === 'yes' ? 'Multi-programme' : (c.is_multi_fee === 'no' ? 'Par programme' : ''),
            'Honoraires (%)': c.is_multi_fee === 'yes' ? this.s(c.duty_free_fee) : '',
            'Boost Commercial': c.multi_boost === 'yes' ? 'Multi-programme' : (c.multi_boost === 'no' ? 'Par programme' : ''),
            'Sig. élec. mandat': this.signatureLabel(c.electronic_signature_mandate),
            'Sig. élec. contrat': this.signatureLabel(c.electronic_signature_contract),
            'Sig. papier contrat': this.signatureLabel(c.paper_signature_contract),
            'CRM': c.crm === 'yes' ? 'Oui' : (c.crm === 'no' ? 'Non' : ''),
            'Fournisseur CRM': this.s(c.supplier),
            'Marge arrière': this.s(c.rear_margin),

            // === COMMENTAIRES ===
            'Commentaires commercial': this.s(c.comments),
            'Commentaires BackOffice': this.s(c.administrative_comment),
            'Compta / Facturation': this.s(c.billing_comments),
            'Marge arrière / Prime': this.s(c.rear_margin_comments),
            'Commentaire dev': this.s(c.dev_comment),
            'Infos Vendeur': this.s(c.seller_infos),
            'Consignes réservation': this.s(c.booking_instructions),
        };
    }
}
